import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

/*
 * The design's lazy clips (`initVideos`). A source is attached only when the element nears the viewport
 * (320 px margin) AND the engine can decode it; a build without H.264 keeps the poster, a real frame of
 * the clip. Nothing plays while paused (reduced motion or the visitor's pause button) or off screen.
 */

function pickSource(video: HTMLVideoElement): string | null {
  const webm = video.getAttribute('data-src-webm');
  const mp4 = video.getAttribute('data-src-mp4');
  const can = (type: string) => typeof video.canPlayType === 'function' && video.canPlayType(type) !== '';
  if (webm && can('video/webm; codecs="vp9,vp8"')) return webm;
  if (mp4 && can('video/mp4; codecs="avc1.42E01E"')) return mp4;
  if (webm && can('video/webm')) return webm;
  if (mp4 && can('video/mp4')) return mp4;
  return null;
}

export function useLazyVideos(containerRef: RefObject<HTMLElement>, paused: boolean): void {
  const pausedRef = useRef(paused);
  const syncAllRef = useRef<() => void>(() => {});

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const videos = Array.from(container.querySelectorAll<HTMLVideoElement>('video[data-lazy-video]'));
    if (!videos.length) return undefined;
    // React sets `muted` only as a property; the attribute is what browsers read for muted-autoplay policy and
    // overlay controls, and it is what the design ships.
    videos.forEach((video) => {
      video.setAttribute('muted', '');
    });
    const cleanups: Array<() => void> = [];

    const loadVideo = (video: HTMLVideoElement, sync: (target: HTMLVideoElement) => void) => {
      if (video.dataset.loaded) return;
      const source = pickSource(video);
      if (!source) {
        video.setAttribute('data-loaded', 'skip');
        return;
      }
      video.setAttribute('data-loaded', '1');
      video.setAttribute('preload', 'metadata');
      video.setAttribute('src', source);
      const onCanPlay = () => sync(video);
      const onError = () => video.removeAttribute('src');
      video.addEventListener('canplay', onCanPlay, { once: true });
      video.addEventListener('error', onError, { once: true });
      cleanups.push(() => {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('error', onError);
      });
    };

    const syncVideo = (video: HTMLVideoElement) => {
      if (pausedRef.current || video.dataset.inview !== '1') {
        if (video.dataset.loaded === '1' && !video.paused) video.pause();
        return;
      }
      loadVideo(video, syncVideo);
      if (video.dataset.loaded !== '1') return;
      const playing = video.play();
      if (playing && typeof playing.catch === 'function') playing.catch(() => {});
    };
    const syncAll = () => videos.forEach(syncVideo);
    syncAllRef.current = syncAll;

    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target as HTMLVideoElement;
            video.setAttribute('data-inview', entry.isIntersecting ? '1' : '0');
            syncVideo(video);
          });
        },
        { rootMargin: '320px 0px', threshold: 0 }
      );
      videos.forEach((video) => observer?.observe(video));
    } else {
      videos.forEach((video) => {
        video.setAttribute('data-inview', '1');
      });
      syncAll();
    }

    // a background tab never decodes: pause on hide, resume on return
    const onVisibility = () => {
      if (!document.hidden) {
        syncAll();
        return;
      }
      videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      cleanups.forEach((cleanup) => cleanup());
      syncAllRef.current = () => {};
      videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
    };
  }, [containerRef]);

  useEffect(() => {
    pausedRef.current = paused;
    syncAllRef.current();
  }, [paused]);
}
